import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Download, FileText, AlertCircle, CheckCircle, MapPin, User, Calendar, FileSearch } from 'lucide-react';
import axios from 'axios';

const ECDisplayPage = () => {
  const navigate = useNavigate();
  const { ecId } = useParams();
  const location = useLocation();
  const [ecData, setEcData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('docx');
  const [filteredTransactions, setFilteredTransactions] = useState(null);
  const [selectedSurveys, setSelectedSurveys] = useState(null);

  useEffect(() => {
    // Check if we have filtered data from survey selection
    if (location.state?.filteredTransactions) {
      setFilteredTransactions(location.state.filteredTransactions);
      setSelectedSurveys(location.state.selectedSurveys);
      setEcData(location.state.originalData);
      setLoading(false);
    } else {
      fetchECData();
    }
  }, [ecId, location.state]);

  const fetchECData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ec/${ecId}`
      );
      
      if (response.data.success) {
        setEcData(response.data.ec_data.extracted_data);
      } else {
        throw new Error('Failed to fetch EC data');
      }
    } catch (err) {
      console.error('Error fetching EC data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load EC data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    
    try {
      const formData = new FormData();
      formData.append('format', selectedFormat);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ec/${ecId}/generate-report`,
        formData,
        {
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ec_report_${ecId}.${selectedFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading EC data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <AlertCircle className="w-8 h-8" />
            <h2 className="text-xl font-bold">Error</h2>
          </div>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/ec-extractor')}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const metadata = ecData?.ec_metadata || {};
  const transactions = filteredTransactions || ecData?.transactions || [];
  
  // Helper function to display values with fallback
  const displayValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return value;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/ec-extractor')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">New Extraction</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="docx">DOCX</option>
                <option value="csv">CSV</option>
              </select>
              
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
              >
                {downloading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download {selectedFormat.toUpperCase()}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Encumbrance Certificate Report</h1>
          <p className="text-gray-600">Extracted data from EC document</p>
        </div>

        {/* Active Filter Banner */}
        {selectedSurveys && selectedSurveys.length > 0 && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-green-900 mb-2">
                  🔍 Filtered View - Survey Numbers: {selectedSurveys.join(', ')}
                </p>
                <p className="text-sm text-green-700">
                  Showing {transactions.length} transaction(s) matching selected survey numbers
                </p>
              </div>
              <button
                onClick={() => navigate(`/ec/select-survey/${ecId}`, {
                  state: { 
                    ecId, 
                    extractedData: ecData,
                    previousSelections: selectedSurveys 
                  }
                })}
                className="px-4 py-2 bg-white text-green-700 border border-green-300 rounded-lg hover:bg-green-100 transition-colors text-sm"
              >
                Change Filter
              </button>
            </div>
          </div>
        )}

        {/* EC Metadata */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <FileSearch className="w-6 h-6 mr-2 text-green-600" />
            EC Metadata
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-l-4 border-green-500 pl-4">
              <p className="text-sm font-medium text-gray-600">Application Number</p>
              <p className="text-lg font-semibold text-gray-900">{displayValue(metadata.application_number)}</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <p className="text-sm font-medium text-gray-600">Statement Number</p>
              <p className="text-lg font-semibold text-gray-900">{displayValue(metadata.statement_number)}</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="text-sm font-medium text-gray-600">Issuing Authority</p>
              <p className="text-lg font-semibold text-gray-900">{displayValue(metadata.issuing_authority)}</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="text-sm font-medium text-gray-600">Issue Date</p>
              <p className="text-lg font-semibold text-gray-900">{displayValue(metadata.issue_date)}</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <p className="text-sm font-medium text-gray-600">Search Period</p>
              <p className="text-lg font-semibold text-gray-900">
                {metadata.period_from && metadata.period_to 
                  ? `${metadata.period_from} to ${metadata.period_to}` 
                  : 'N/A'}
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <p className="text-sm font-medium text-gray-600">Search Period (Years)</p>
              <p className="text-lg font-semibold text-gray-900">{displayValue(metadata.search_period_years)}</p>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-orange-600" />
            Transaction History ({transactions.length})
          </h2>
          
          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">S.No.</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Doc No.</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Reg Date</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Exec Date</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Executants</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Claimants</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Survey No.</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Extent</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Link Doct</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => {
                    // Handle linked_documents as string or array
                    const linkedDocs = transaction.linked_documents;
                    let linkedText = 'N/A';
                    if (linkedDocs) {
                      if (Array.isArray(linkedDocs)) {
                        linkedText = linkedDocs.length > 0 ? linkedDocs.join(', ') : 'N/A';
                      } else if (typeof linkedDocs === 'string' && linkedDocs.trim() !== '') {
                        linkedText = linkedDocs;
                      }
                    }
                    
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.serial_number)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.document_number)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.registration_date)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.execution_date)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.executants)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.claimants)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.survey_number)}</td>
                        <td className="px-3 py-2 text-gray-900">{displayValue(transaction.extent)}</td>
                        <td className="px-3 py-2 text-gray-900 text-xs">{linkedText}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No transactions found</p>
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default ECDisplayPage;
